import React, { Component } from 'react';
import { NetworkError } from '@/layout/common-pages';
import PropTypes from 'prop-types';

/**
 * React Error Boundary
 * Catches JavaScript errors anywhere in the child component tree
 * Logs error details and displays fallback UI
 *
 * Note: Error boundaries do NOT catch errors in:
 * - Event handlers (use try-catch)
 * - Asynchronous code (setTimeout, promises)
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 */
class ErrorBoundary extends Component {
  /**
   * Constructor - initializes error boundary state
   * @param {Object} props - Component props
   */
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Static method called when an error is thrown
   * @param {Error} error - The error that was thrown
   * @return {Object} Updated state object
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  /**
   * Lifecycle method called after an error has been thrown
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Object with componentStack key
   */
  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error Boundary Caught:', error, errorInfo);

    // Store error info for display
    this.setState({ errorInfo });

    // Send to error reporting service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Could integrate with error tracking service
    // logErrorToService(error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Render method
   * @return {JSX.Element} Either fallback UI or children
   */
  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback(this.state.error, this.resetError)
          : this.props.fallback;
      }

      // Default fallback UI
      return (
        <NetworkError
          error={{
            message:
              this.state.error?.message ||
              'An unexpected error occurred. Please try again.',
            stack: this.state.errorInfo?.componentStack,
          }}
        />
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onError: PropTypes.func,
};

export { ErrorBoundary };
